import React, { useEffect } from "react";
import { Layout } from "../layout";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import { OutlineSelect, OutlineTextField } from "~/components";
import { DialogRow } from "./components/DialogRow";
import { sex } from "../../../const/options";
import { convertLabel } from "~/helpers/select";
import { useModule } from "./hooks/index";
import { useSelector } from "react-redux";
import { RootState } from "~/redux/rootReducer";

type Props = {
  type?: "update" | "registor";
};

export const SpecialistRegistor: React.VFC<Props> = (props) => {
  const classes = useStyles();

  // global state
  const { uid, data } = useSelector((state: RootState) => state.specialistAuth);
  const { prefectures, businesses, contacts } = useSelector(
    (state: RootState) => state.master
  );

  // 編集の時のみ、専門家データをセット
  useEffect(() => {
    if (props.type === "update") {
      if (data) setSpecialist(data);
    }
  }, [data]);

  // hooks
  const {
    states: { specialist, errorAlertMessage, errorMessage, openModal, loading },
    setters: { changeValue, setSpecialist },
    actions: {
      openModalWithValidate,
      closeModal,
      registorSpecialist,
      updateSpecialist,
    },
  } = useModule();

  // 登録 or 更新
  let handleOpenConfirmModal: () => void;
  let handleSubmit: () => Promise<void>;
  if (props.type === "update") {
    // 編集の時
    handleOpenConfirmModal = () => openModalWithValidate({ auth: false });
    handleSubmit = () => updateSpecialist(uid);
  } else {
    // 新規登録の時
    handleOpenConfirmModal = () => openModalWithValidate({ auth: true });
    handleSubmit = () => registorSpecialist();
  }

  return (
    <Layout loading={loading} errorMessage={errorAlertMessage}>
      {props.type !== "update" ? (
        <>
          <h1 className={classes.title}>新規登録</h1>
          <p className={classes.text}>
            以下のフォームに記入してください。※は必須項目です。
          </p>
          <p className={classes.text}>
            登録を希望する方は必ず（右上の）利用規約・登録専門家規約・個人情報保護方針を確認してください
          </p>
        </>
      ) : (
        <>
          <h1 className={classes.title}>情報編集</h1>
          <p className={classes.text}>
            変更や追加がある情報を入力してください。※は必須項目です。
          </p>
        </>
      )}
      <div className={classes.formWrapper}>
        {props.type === "update" && (
          <OutlineSelect
            label={`公開状態（${
              specialist.isPublic
                ? "アプリ上に情報が公開されています"
                : "運営会社によって承認されると公開になります"
            }）`}
            options={[
              { value: 1, label: "公開" },
              { value: 0, label: "非公開" },
            ]}
            value={specialist.isPublic ? 1 : 0}
            required
          />
        )}
        <OutlineTextField
          label="苗字(漢字)*"
          value={specialist.lastName}
          onChange={(e) => changeValue(e, "lastName")}
          error={errorMessage.lastName ? true : false}
          helperText={errorMessage.lastName}
        />
        <OutlineTextField
          label="名前(漢字)*"
          value={specialist.firstName}
          onChange={(e) => changeValue(e, "firstName")}
          error={errorMessage.firstName ? true : false}
          helperText={errorMessage.firstName}
        />
        <OutlineTextField
          label="苗字(カタカナ)*"
          value={specialist.lastNameKana}
          onChange={(e) => changeValue(e, "lastNameKana")}
          error={errorMessage.lastNameKana ? true : false}
          helperText={errorMessage.lastNameKana}
        />
        <OutlineTextField
          label="名前(カタカナ)*"
          value={specialist.firstNameKana}
          onChange={(e) => changeValue(e, "firstNameKana")}
          error={errorMessage.firstNameKana ? true : false}
          helperText={errorMessage.firstNameKana}
        />
        <OutlineSelect
          label="性別*"
          options={sex}
          value={specialist.sex}
          onChange={(e) => changeValue(e, "sex")}
          error={errorMessage.sex ? true : false}
          helperText={errorMessage.sex}
        />
        {props.type !== "update" && (
          <>
            <OutlineTextField
              label="メールアドレス*"
              value={specialist.email}
              onChange={(e) => changeValue(e, "email")}
              error={errorMessage.email ? true : false}
              helperText={errorMessage.email}
              type="email"
            />
            <OutlineTextField
              label="パスワード*"
              value={specialist.password}
              onChange={(e) => changeValue(e, "password")}
              error={errorMessage.password ? true : false}
              helperText={errorMessage.password}
              type="password"
            />
            <OutlineTextField
              label="パスワード(再確認)*"
              value={specialist.confirmPassword}
              onChange={(e) => changeValue(e, "confirmPassword")}
              error={errorMessage.confirmPassword ? true : false}
              helperText={errorMessage.confirmPassword}
              type="password"
            />
          </>
        )}
        <OutlineSelect
          label="業態*"
          options={businesses}
          value={specialist.business}
          onChange={(e) => changeValue(e, "business")}
          error={errorMessage.business ? true : false}
          helperText={errorMessage.business}
        />
        <OutlineSelect
          label="活動エリア"
          options={prefectures}
          value={specialist.prefecture}
          onChange={(e) => changeValue(e, "prefecture")}
          error={errorMessage.prefecture ? true : false}
          helperText={errorMessage.prefecture}
        />
        <OutlineTextField
          label="所属(企業、団体名)"
          value={specialist.company}
          onChange={(e) => changeValue(e, "company")}
          error={errorMessage.company ? true : false}
          helperText={errorMessage.company}
        />
        <OutlineTextField
          label="事務所の住所"
          value={specialist.address}
          onChange={(e) => changeValue(e, "address")}
          error={errorMessage.address ? true : false}
          helperText={errorMessage.address}
        />
        <OutlineTextField
          label="ホームページ"
          value={specialist.hp}
          onChange={(e) => changeValue(e, "hp")}
          error={errorMessage.hp ? true : false}
          helperText={errorMessage.hp}
        />
        <OutlineSelect
          label="お客様にご希望する連絡手段①*"
          options={contacts}
          value={specialist.contactMethod1}
          onChange={(e) => changeValue(e, "contactMethod1")}
          error={errorMessage.contactMethod1 ? true : false}
          helperText={errorMessage.contactMethod1}
        />
        <OutlineTextField
          label="連絡手段①の連絡先(メールアドレス、ID、住所、電話番号、URL)*"
          value={specialist.contact1}
          onChange={(e) => changeValue(e, "contact1")}
          error={errorMessage.contact1 ? true : false}
          helperText={errorMessage.contact1}
        />
        <OutlineSelect
          label="お客様にご希望する連絡手段②"
          options={contacts}
          value={specialist.contactMethod2}
          onChange={(e) => changeValue(e, "contactMethod2")}
          error={errorMessage.contactMethod2 ? true : false}
          helperText={errorMessage.contactMethod2}
        />
        <OutlineTextField
          label="連絡手段②の連絡先(メールアドレス、ID、住所、電話番号、URL)"
          value={specialist.contact2}
          onChange={(e) => changeValue(e, "contact2")}
          error={errorMessage.contact2 ? true : false}
          helperText={errorMessage.contact2}
        />
        <OutlineTextField
          label="自己紹介文"
          value={specialist.introduction}
          onChange={(e) => changeValue(e, "introduction")}
          error={errorMessage.introduction ? true : false}
          helperText={errorMessage.introduction}
          multiline
          rows={10}
        />
        <Button
          onClick={handleOpenConfirmModal}
          className={classes.confirmButton}
          variant="contained"
          color="secondary"
        >
          確認画面へ
        </Button>
      </div>

      <Dialog
        open={openModal}
        onClose={closeModal}
        fullWidth={true}
        maxWidth="md"
      >
        <DialogTitle>内容をご確認ください</DialogTitle>
        {props.type !== "update" ? (
          // 新規登録
          <p className={classes.dialogText}>
            ※&nbsp;本サイトでの新規登録は専門家としての仮登録を意味します。この後契約の締結などを経て本登録されて初めて情報が公開されます。
          </p>
        ) : (
          <p className={classes.dialogText}>
            ※&nbsp;情報を更新すると、非公開設定になります。ご注意ください。
          </p>
        )}
        <DialogContent className={classes.dialogContent}>
          <DialogRow label="苗字(漢字)*" value={specialist.lastName} />
          <DialogRow label="名前(漢字)*" value={specialist.firstName} />
          <DialogRow label="苗字(カタカナ)*" value={specialist.lastNameKana} />
          <DialogRow label="名前(カタカナ)*" value={specialist.firstNameKana} />
          <DialogRow label="性別*" value={convertLabel(sex, specialist.sex)} />
          {props.type !== "update" && (
            <DialogRow label="メールアドレス*" value={specialist.email} />
          )}
          <DialogRow
            label="業態*"
            value={convertLabel(businesses, specialist.business)}
          />
          <DialogRow
            label="活動エリア"
            value={convertLabel(prefectures, specialist.prefecture)}
          />
          <DialogRow label="所属(企業、団体名)" value={specialist.company} />
          <DialogRow label="事務所の住所" value={specialist.address} />
          <DialogRow label="ホームページ" value={specialist.hp} />
          <DialogRow
            label="お客様にご希望する連絡手段①*"
            value={convertLabel(contacts, specialist.contactMethod1)}
          />
          <DialogRow
            label={
              "連絡手段①の連絡先\n(メールアドレス、ID、住所、電話番号、URL)*"
            }
            value={specialist.contact1}
          />
          <DialogRow
            label="お客様にご希望する連絡手段②"
            value={convertLabel(contacts, specialist.contactMethod2)}
          />
          <DialogRow
            label={
              "連絡手段②の連絡先\n(メールアドレス、ID、住所、電話番号、URL)"
            }
            value={specialist.contact2}
          />
          <DialogRow label="自己紹介文" value={specialist.introduction} />
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={closeModal}>
            閉じる
          </Button>
          <Button color="secondary" variant="contained" onClick={handleSubmit}>
            {props.type !== "update" ? "登録" : "更新"}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

const useStyles = makeStyles({
  title: {
    fontSize: 34,
    fontWeight: "normal",
    margin: 0,
    marginTop: 70,
    marginBottom: 20,
  },
  text: {
    fontSize: 20,
    margin: 0,
    marginBottom: 10,
  },
  formWrapper: {
    marginTop: 50,
    marginBottom: 120,
  },
  confirmButton: {
    marginTop: 20,
    fontSize: 18,
    width: 160,
  },
  dialogText: {
    margin: 20,
    marginTop: 0,
  },
  dialogContent: {
    paddingTop: 15,
    paddingBottom: 30,
  },
});
